import api from './api';
import type {
  AccountStatus,
  AdminAlertsResponse,
  AdminUserRow,
  AdminUsersFilters,
  ApiUsageResponse,
  AuthEvent,
  AuthEventsFilters,
  BlockIpInput,
  BlockedIp,
  CreatorDataFlagsResponse,
  CreatorDataRequestsResponse,
  ExtractorHealthResponse,
  ForceLogoutResult,
  GrowthAccountsFilters,
  GrowthAccountsResponse,
  GrowthOverview,
  HelpChatLogRow,
  HelpChatLogsFilters,
  HelpChatSession,
  HelpChatStats,
  OrgStatus,
  OrgStatusResult,
  Paginated,
  PoolStatsResponse,
  SecurityEvent,
  SecurityEventsFilters,
  SecurityOverview,
  TrafficReport,
  TrafficScope,
  UserStatusResult,
} from '../types/admin';
import type { GlobalRole } from '../types';

type ParamValue = string | number | boolean | null | undefined;

/** Drop empty/undefined params so URLs stay clean and the API sees "unset". */
function cleanParams(params: Record<string, ParamValue>): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '' || value === false) continue;
    out[key] = value === true ? 1 : value;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Users
// ---------------------------------------------------------------------------

export async function listAdminUsers(filters: AdminUsersFilters): Promise<Paginated<AdminUserRow>> {
  const res = await api.get<Paginated<AdminUserRow>>('/admin/users', { params: cleanParams({ ...filters }) });
  return res.data;
}

export async function updateAdminUserRole(userId: string, role: GlobalRole): Promise<void> {
  await api.patch(`/admin/users/${userId}`, { role });
}

export async function setAdminUserStatus(
  userId: string,
  body: { status: AccountStatus; reason?: string }
): Promise<UserStatusResult> {
  const res = await api.post<UserStatusResult>(`/admin/users/${userId}/status`, body);
  return res.data;
}

export async function forceLogoutAdminUser(userId: string): Promise<ForceLogoutResult> {
  const res = await api.post<ForceLogoutResult>(`/admin/users/${userId}/force-logout`);
  return res.data;
}

export async function setAdminOrgStatus(
  organizationId: string,
  body: { status: OrgStatus; reason?: string }
): Promise<OrgStatusResult> {
  const res = await api.post<OrgStatusResult>(`/admin/organizations/${organizationId}/status`, body);
  return res.data;
}

// ---------------------------------------------------------------------------
// Help chat
// ---------------------------------------------------------------------------

export async function listHelpChatLogs(filters: HelpChatLogsFilters): Promise<Paginated<HelpChatLogRow>> {
  const res = await api.get<Paginated<HelpChatLogRow>>('/admin/help-chat/logs', {
    params: cleanParams({ ...filters }),
  });
  return res.data;
}

export async function getHelpChatLog(id: string): Promise<HelpChatLogRow> {
  const res = await api.get<HelpChatLogRow>(`/admin/help-chat/logs/${encodeURIComponent(id)}`);
  return res.data;
}

export async function getHelpChatSession(sessionId: string): Promise<HelpChatSession> {
  const res = await api.get<HelpChatSession>(`/admin/help-chat/sessions/${encodeURIComponent(sessionId)}`);
  return res.data;
}

export async function getHelpChatStats(days: number): Promise<HelpChatStats> {
  const res = await api.get<HelpChatStats>('/admin/help-chat/stats', { params: { days } });
  return res.data;
}

// ---------------------------------------------------------------------------
// Security
// ---------------------------------------------------------------------------

export async function getSecurityOverview(hours: number): Promise<SecurityOverview> {
  const res = await api.get<SecurityOverview>('/admin/security/overview', { params: { hours } });
  return res.data;
}

export async function listSecurityEvents(filters: SecurityEventsFilters): Promise<Paginated<SecurityEvent>> {
  const res = await api.get<Paginated<SecurityEvent>>('/admin/security/events', {
    params: cleanParams({ ...filters }),
  });
  return res.data;
}

export async function resolveSecurityEvent(id: string): Promise<SecurityEvent> {
  const res = await api.post<SecurityEvent>(`/admin/security/events/${encodeURIComponent(id)}/resolve`);
  return res.data;
}

export async function listAuthEvents(filters: AuthEventsFilters): Promise<Paginated<AuthEvent>> {
  const res = await api.get<Paginated<AuthEvent>>('/admin/security/auth-events', {
    params: cleanParams({ ...filters }),
  });
  return res.data;
}

export async function getTrafficReport(scope: TrafficScope, hours = 168): Promise<TrafficReport> {
  const res = await api.get<TrafficReport>('/admin/security/traffic', {
    params: cleanParams({ ...scope, hours }),
  });
  return res.data;
}

export async function listBlockedIps(): Promise<{ items: BlockedIp[] }> {
  const res = await api.get<{ items: BlockedIp[] }>('/admin/security/blocked-ips');
  return res.data;
}

export async function blockIp(input: BlockIpInput): Promise<BlockedIp> {
  const body: Record<string, unknown> = { ip: input.ip.trim() };
  if (input.reason?.trim()) body.reason = input.reason.trim();
  if (input.expiresInHours != null) body.expiresInHours = input.expiresInHours;
  if (input.force) body.force = true;
  const res = await api.post<BlockedIp>('/admin/security/blocked-ips', body);
  return res.data;
}

export async function unblockIp(id: string): Promise<void> {
  await api.delete(`/admin/security/blocked-ips/${encodeURIComponent(id)}`);
}

// ---------------------------------------------------------------------------
// Alerts
// ---------------------------------------------------------------------------

export async function listAdminAlerts(params: {
  acknowledged?: '0' | '1';
  page?: number;
  limit?: number;
}): Promise<AdminAlertsResponse> {
  const res = await api.get<AdminAlertsResponse>('/admin/alerts', { params: cleanParams({ ...params }) });
  return res.data;
}

export async function getUnackedAlertCount(): Promise<{ count: number }> {
  const res = await api.get<{ count: number }>('/admin/alerts/unacked-count');
  return res.data;
}

export async function ackAdminAlert(id: string): Promise<void> {
  await api.post(`/admin/alerts/${encodeURIComponent(id)}/ack`);
}

export async function sendTestAlertEmail(): Promise<{ sentTo: string }> {
  const res = await api.post<{ sentTo: string }>('/admin/alerts/test-email');
  return res.data;
}

/** Absolute URL for the HTML digest preview (opened in a new tab, cookies included). */
export function digestPreviewUrl(): string {
  const base = api.defaults.baseURL || '/api';
  return `${base.replace(/\/+$/, '')}/admin/alerts/digest-preview`;
}

// ---------------------------------------------------------------------------
// Growth
// ---------------------------------------------------------------------------

export async function getGrowthOverview(weeks: number): Promise<GrowthOverview> {
  const res = await api.get<GrowthOverview>('/admin/growth/overview', { params: { weeks } });
  return res.data;
}

export async function listGrowthAccounts(filters: GrowthAccountsFilters): Promise<GrowthAccountsResponse> {
  const res = await api.get<GrowthAccountsResponse>('/admin/growth/accounts', {
    params: cleanParams({ ...filters }),
  });
  return res.data;
}

// ----- Data sources (Phase 2 creator pipeline) -----

export async function getExtractorHealth(hours: number): Promise<ExtractorHealthResponse> {
  const res = await api.get<ExtractorHealthResponse>('/admin/data-sources/extractors', {
    params: { hours },
  });
  return res.data;
}

export async function listCreatorDataFlags(filters: {
  status?: string;
  page?: number;
  limit?: number;
}): Promise<CreatorDataFlagsResponse> {
  const res = await api.get<CreatorDataFlagsResponse>('/admin/data-sources/flags', {
    params: filters,
  });
  return res.data;
}

export async function resolveCreatorDataFlag(
  id: string,
  status: 'ACCEPTED' | 'REJECTED' | 'QUARANTINED'
): Promise<{ ok: boolean; status: string }> {
  const res = await api.patch<{ ok: boolean; status: string }>(
    `/admin/data-sources/flags/${id}`,
    { status }
  );
  return res.data;
}

export async function listCreatorDataRequests(filters: {
  status?: string;
  page?: number;
  limit?: number;
}): Promise<CreatorDataRequestsResponse> {
  const res = await api.get<CreatorDataRequestsResponse>('/admin/data-sources/creator-requests', {
    params: filters,
  });
  return res.data;
}

/**
 * The Tier 4 backstop: an admin who has satisfied themselves out-of-band that the requester
 * controls the account. APPLIED runs the same suppression the automated path runs.
 */
export async function resolveCreatorDataRequest(
  id: string,
  status: 'APPLIED' | 'REJECTED',
  notes?: string
): Promise<{ ok: boolean; status: string; readingsDeleted?: number }> {
  const res = await api.patch<{ ok: boolean; status: string; readingsDeleted?: number }>(
    `/admin/data-sources/creator-requests/${id}`,
    { status, notes }
  );
  return res.data;
}

export async function getApiUsage(days: number): Promise<ApiUsageResponse> {
  const res = await api.get<ApiUsageResponse>('/admin/data-sources/api-usage', {
    params: { days },
  });
  return res.data;
}

export async function getPoolStats(): Promise<PoolStatsResponse> {
  const res = await api.get<PoolStatsResponse>('/admin/data-sources/pool');
  return res.data;
}
